import {API} from "aws-amplify";
import {getAPIInputContext} from "../../../common/utils/apiUtils";
import {FILING_FETCH_CREDENTIAL_AUDIT, FILING_UPLOAD_CREDENTIAL} from "../../../ApiPaths";


/**
 * This function will fetch credential audit data from CFS via TwinService
 * @param setCredentialData - Function to set credential data state
 * @param useCase - The selected country from the Country dropdown
 * @param subUseCase - The selected compliance from the Compliance dropdown
 * @param setError - Function to set error state
 * @param metaData - Additional metadata for the request
 * @returns {Promise} - Returns a promise for the component to handle loading states
 */
export function fetchCredentialAuditData(setCredentialData, useCase, subUseCase, setError = null, metaData = {}) {
    return getAPIInputContext().then(input => {
        input.body.requester = input.body.user;
        input.body.useCase = useCase;
        input.body.subUseCase = subUseCase;
        input.body.metaData = metaData;

        API.post("TWIN_API", FILING_FETCH_CREDENTIAL_AUDIT, input).then(resp => {
            if (resp.status === 200) {
                const credentialData = JSON.parse(resp.body.credentialData || '[]');
                setCredentialData(credentialData);
                setError?.(null);
            }
            else if(resp.status === 401){
                console.error(`You are not authorised`);
                if (setError) setError({ type: 'unauthorized', message: `You are not authorized to view credential details of Country : ${useCase} and Compliance : ${subUseCase}` });
                setCredentialData([]);
            }
            else {
                console.error(`FILING_FETCH_CREDENTIAL_AUDIT API responded with status ${resp.status}`);
                if (setError) setError({ type: 'error', message: `Failed to fetch credential data for Country : ${useCase} and Compliance : ${subUseCase}. Status: ${resp.status}` });
                setCredentialData([]);
            }
        })
            .catch(error => {
                console.error("FILING_FETCH_CREDENTIAL_AUDIT API Error:", error);
                if (setError) setError({ type: 'error', message: `Failed to fetch credential data for Country : ${useCase} and Compliance : ${subUseCase}. Please try again.` });
                setCredentialData([]);
            })
    });
}

/**
 * This function will upload credential audit data from CFS via TwinService
 * @param setUploadState - Function to set Upload data state
 * @param options - Object containing upload parameters
 * @param options.useCase - The selected country from the Country dropdown
 * @param options.subUseCase - The selected compliance from the Compliance dropdown
 * @param options.credentialType - The selected credential Type
 * @param options.s3Key - s3Key of the uploaded credential
 * @param options.s3Bucket - s3Bucket of the uploaded credential
 * @param options.expiryAt - expiryDate of the uploaded credential (optional)
 * @param options.setError - Function to set error state
 * @param options.metaData - Additional metadata for the request
 * @returns {Promise} - Returns a promise for the component to handle loading states
 */

export function uploadCredentials(setUploadState, options = {}) {
    const { useCase, subUseCase, credentialType, s3Key, s3Bucket, expiryAt, setError = null, metaData = {} } = options;
    return getAPIInputContext().then(input => {
        input.body.requester = input.body.user;
        input.body.useCase = useCase;
        input.body.subUseCase = subUseCase;
        input.body.credentialType = credentialType;
        input.body.s3Key = s3Key;
        input.body.s3Bucket = s3Bucket;

        if (expiryAt) {
            input.body.expiryAt = expiryAt;
        }

        input.body.metaData = metaData;

        return API.post("TWIN_API", FILING_UPLOAD_CREDENTIAL, input)
            .then(resp => {
                const isSuccess = resp?.status === 200 && resp?.body?.status === 'SUCCESS';
                const isPartialSuccess = resp?.status === 200 && resp?.body?.status === 'PARTIAL_SUCCESS';
                const isFailed = resp?.status === 200 && resp?.body?.status === 'FAILED';
                const failedMap = resp?.body?.failedFilingIdentifierToErrorMessageMap || {};

                if (!isSuccess) {
                    if (isPartialSuccess) {
                        console.warn("PARTIAL SUCCESS with errors in failed map:", failedMap);
                        setUploadState(resp.body);
                        if (setError) setError({
                            type: 'partial-success',
                            message: `Some credentials failed to upload for Country : ${useCase} and Compliance : ${subUseCase}. with following: ${failedMap}`
                        });
                        return;
                    }
                    if(isFailed){
                        console.error("Failure with the following errors:",failedMap)
                        setUploadState(resp.body);
                        if (setError) setError({
                            type: 'failure',
                            message: `All credentials failed to upload for Country : ${useCase} and Compliance : ${subUseCase} with the following: ${failedMap}`
                        });
                        return;
                    }
                    if (resp?.status === 401) {
                        console.error("Unauthorized");
                        if (setError) setError({
                            type: 'unauthorized',
                            message: `You are not authorized to view credential details of Country : ${useCase} and Compliance : ${subUseCase}`
                        });
                        setUploadState(null);
                        return;
                    }
                    if (!resp?.status && !resp?.body) {
                        console.log("Found error message in direct response:", resp.message);
                        setUploadState(resp);
                        if (setError) setError({
                            type: 'unauthorized',
                            message: `Internal error occurred while uploading credentials of Country : ${useCase} and Compliance : ${subUseCase}`
                        });
                    }

                } else {
                    console.log("SUCCESS");
                    setUploadState(resp.body);
                    setError?.(null);
                }
            })
            .catch(error => {
                console.error("Error response data:", error.response?.data);
                let errorMessage = `Failed to upload credential data for Country : ${useCase} and Compliance : ${subUseCase}. Please try again.`;
                const data = error?.response?.data;

                if (typeof data === 'string') {
                    try {
                        const parsedData = JSON.parse(data);
                        if (parsedData.message) {
                            errorMessage = parsedData.message;
                            setUploadState(parsedData);
                            setError?.(null);
                            return;
                        }
                    } catch {
                        console.warn("Could not parse string response as JSON");
                    }
                } else if (data?.message) {
                    errorMessage = data.message;
                    setUploadState(data);
                    setError?.(null);
                    return;
                }

                setUploadState(null);
                if (setError) setError({ type: 'error', message: errorMessage });
            });
    });
}
